import React, { useState } from 'react';
import { X, Save, Edit, CheckCircle, XCircle } from 'lucide-react';
import { clsx } from 'clsx';
import MathRenderer from '../math/MathRenderer';

const StudentResultModal = ({ submission, assessment, onClose, onGradeUpdate }) => {
  const [isGrading, setIsGrading] = useState(false);
  const [manualGrades, setManualGrades] = useState({});
  const [feedback, setFeedback] = useState(submission.feedback || '');

  const questions = assessment?.questions || [];

  const getQuestionScore = (question) => {
    // Auto-graded questions
    if (question.type === 'multiple-choice' || question.type === 'true-false') {
      const answer = submission.answers[question.id];
      const isCorrect = answer == question.correctAnswer;
      return isCorrect ? question.points : 0;
    }
    
    // Manual graded questions
    const manualGrade = manualGrades[question.id] || submission.manualGrades?.[question.id];
    return manualGrade !== undefined ? manualGrade : null;
  };

  const getTotalScore = () => {
    let total = 0;
    let hasUngraded = false;

    questions.forEach(question => {
      const score = getQuestionScore(question);
      if (score !== null) {
        total += score;
      } else {
        hasUngraded = true;
      }
    });

    return { total, hasUngraded };
  };

  const handleGradeChange = (questionId, grade) => {
    setManualGrades(prev => ({
      ...prev,
      [questionId]: parseFloat(grade) || 0
    }));
  };

  const handleSaveGrades = async () => {
    try {
      const { total } = getTotalScore();
      
      // Update submission with new grades
      await onGradeUpdate(submission.id, {
        manualGrades,
        totalScore: total,
        feedback,
        gradedAt: new Date(),
        status: 'graded'
      });
      
      setIsGrading(false);
    } catch (error) {
      console.error('Error saving grades:', error);
    }
  };

  const { total: currentTotal, hasUngraded } = getTotalScore();
  const maxScore = questions.reduce((sum, q) => sum + q.points, 0);
  const percentage = maxScore > 0 ? (currentTotal / maxScore) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {submission.studentName}'s Submission
            </h2>
            <p className="text-sm text-gray-500">{assessment.title}</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {submission.status === 'pending' && (
              <button
                onClick={() => setIsGrading(!isGrading)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Edit className="w-4 h-4 mr-2" />
                {isGrading ? 'Cancel' : 'Grade'}
              </button>
            )}
            
            {isGrading && (
              <button
                onClick={handleSaveGrades}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-sigma-600 hover:bg-sigma-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Grades
              </button>
            )}
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Submission Info */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm font-medium text-gray-500">Score</div>
              <div className="text-lg font-semibold text-gray-900">
                {hasUngraded ? (
                  <span className="text-yellow-600">Pending</span>
                ) : (
                  <>
                    {currentTotal}/{maxScore}
                    <span className="text-sm text-gray-500 ml-2">
                      ({percentage.toFixed(1)}%)
                    </span>
                  </>
                )}
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-500">Status</div>
              <div className="flex items-center mt-1">
                {submission.status === 'graded' ? (
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                ) : (
                  <XCircle className="w-4 h-4 text-yellow-500 mr-2" />
                )}
                <span className="text-sm capitalize">{submission.status}</span>
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-500">Time Spent</div>
              <div className="text-sm text-gray-900">
                {Math.floor(submission.timeSpent / 60)}:{(submission.timeSpent % 60).toString().padStart(2, '0')}
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-500">Submitted</div>
              <div className="text-sm text-gray-900">
                {submission.submittedAt.toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* Questions and Answers */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {questions.map((question, index) => {
              const answer = submission.answers[question.id];
              const score = getQuestionScore(question);
              const isCorrect = score === question.points;
              
              return (
                <div key={question.id} className="border border-gray-200 rounded-lg p-6">
                  {/* Question Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-sigma-100 rounded-full">
                        <span className="text-sm font-medium text-sigma-700">{index + 1}</span>
                      </div>
                      <span className="text-sm text-gray-500 capitalize">
                        {question.type.replace('-', ' ')}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {/* Score Display/Input */}
                      {question.type === 'multiple-choice' || question.type === 'true-false' ? (
                        <div className={clsx(
                          'flex items-center px-2 py-1 rounded text-sm font-medium',
                          isCorrect 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        )}>
                          {isCorrect ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                          {score}/{question.points}
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          {isGrading ? (
                            <input
                              type="number"
                              min="0"
                              max={question.points}
                              step="0.5"
                              value={manualGrades[question.id] || ''}
                              onChange={(e) => handleGradeChange(question.id, e.target.value)}
                              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                            />
                          ) : (
                            <span className="text-sm font-medium">
                              {score !== null ? `${score}/` : 'Ungraded/'}
                            </span>
                          )}
                          <span className="text-sm text-gray-500">{question.points}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Question Content */}
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">{question.title}</h4>
                    
                    {question.type === 'math' && question.mathContent ? (
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <MathRenderer latex={question.mathContent} />
                      </div>
                    ) : question.content && (
                      <p className="text-gray-700 whitespace-pre-wrap">{question.content}</p>
                    )}
                  </div>

                  {/* Student Answer */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Student Answer:</div>
                    
                    {question.type === 'math' && answer ? (
                      <div className="p-3 bg-blue-50 rounded border">
                        <MathRenderer latex={answer} />
                      </div>
                    ) : question.type === 'multiple-choice' ? (
                      <div className="space-y-2">
                        {question.options?.map((option, optionIndex) => (
                          <div
                            key={optionIndex}
                            className={clsx(
                              'flex items-center p-2 rounded border',
                              answer == optionIndex
                                ? question.correctAnswer == optionIndex
                                  ? 'bg-green-50 border-green-200 text-green-800'
                                  : 'bg-red-50 border-red-200 text-red-800'
                                : question.correctAnswer == optionIndex
                                  ? 'bg-gray-50 border-gray-200 text-green-600'
                                  : 'bg-gray-50 border-gray-200'
                            )}
                          >
                            <span className="font-medium mr-2">
                              {String.fromCharCode(65 + optionIndex)}.
                            </span>
                            <span>{option}</span>
                            {answer == optionIndex && (
                              <span className="ml-auto text-xs font-medium">
                                Student's Answer
                              </span>
                            )}
                            {question.correctAnswer == optionIndex && answer != optionIndex && (
                              <span className="ml-auto text-xs font-medium text-green-600">
                                Correct Answer
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : question.type === 'true-false' ? (
                      <div className="space-y-2">
                        {['true', 'false'].map((option) => (
                          <div
                            key={option}
                            className={clsx(
                              'flex items-center p-2 rounded border',
                              answer === option
                                ? question.correctAnswer === option
                                  ? 'bg-green-50 border-green-200 text-green-800'
                                  : 'bg-red-50 border-red-200 text-red-800'
                                : question.correctAnswer === option
                                  ? 'bg-gray-50 border-gray-200 text-green-600'
                                  : 'bg-gray-50 border-gray-200'
                            )}
                          >
                            <span className="capitalize">{option}</span>
                            {answer === option && (
                              <span className="ml-auto text-xs font-medium">
                                Student's Answer
                              </span>
                            )}
                            {question.correctAnswer === option && answer !== option && (
                              <span className="ml-auto text-xs font-medium text-green-600">
                                Correct Answer
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 rounded border">
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {answer || <span className="text-gray-400 italic">No answer provided</span>}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Correct Answer/Explanation */}
                  {question.explanation && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="text-xs font-medium text-blue-800 mb-1">Explanation:</div>
                      <div className="text-sm text-blue-700">{question.explanation}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Overall Feedback */}
          <div className="mt-6 border-t border-gray-200 pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Overall Feedback
            </label>
            {isGrading ? (
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                placeholder="Provide feedback for the student..."
              />
            ) : feedback ? (
              <div className="p-3 bg-gray-50 rounded border">
                <p className="text-gray-700 whitespace-pre-wrap">{feedback}</p>
              </div>
            ) : (
              <p className="text-gray-400 italic">No feedback provided</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentResultModal;